import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { btnGhost, Modal } from './components/Modal';

/**
 * Work that exists only in the browser, and the one question worth asking before it is lost.
 *
 * The template editor is the first thing here to hold a draft: everything else in this app either
 * saves on the click that changes something, or lives inside a dialog you cannot navigate past.
 * An editor is different — the whole screen is a draft, and every way out of it is an ordinary
 * control that looks harmless.
 *
 * One flag and one dialog, held here rather than in the editor, because the ways out are spread
 * across components that know nothing about templates: the section nav, the room switcher, the
 * editor's own back button, and the browser's own close. Each of them routes through `leave`, and
 * only the component actually holding the draft has to say that it has one.
 */

interface UnsavedValue {
  /** Whether something would be lost right now. */
  dirty: boolean;
  /** Declared by whatever holds the draft; see `useUnsavedWork`. */
  setDirty: (dirty: boolean) => void;
  /** Runs `go` now, or asks first when there is unsaved work. */
  leave: (go: () => void) => void;
}

const UnsavedContext = createContext<UnsavedValue | null>(null);

export function useUnsaved(): UnsavedValue {
  const ctx = useContext(UnsavedContext);
  if (!ctx) throw new Error('useUnsaved must be used within an UnsavedProvider.');
  return ctx;
}

/**
 * Declares that this component is holding unsaved work, for as long as it is mounted.
 *
 * Clearing on unmount matters: an editor closed by a route that didn't ask — a save that
 * navigates on success, say — must not leave the flag set and make the next click ask about a
 * draft nobody has.
 */
export function useUnsavedWork(dirty: boolean): void {
  const { setDirty } = useUnsaved();
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);
}

export function UnsavedProvider({ children }: { children: ReactNode }) {
  const [dirty, setDirty] = useState(false);
  // Held as an object rather than the function itself: `setState` would call a bare function
  // instead of storing it.
  const [pending, setPending] = useState<{ go: () => void } | null>(null);

  const leave = useCallback(
    (go: () => void) => {
      if (dirty) setPending({ go });
      else go();
    },
    [dirty],
  );

  // Closing the tab or reloading is the one exit this app cannot intercept itself. Browsers show
  // their own wording here and ignore ours, which is why there is no message to pass.
  useEffect(() => {
    if (!dirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Deprecated, and still what some engines actually check. The string is ignored — every
      // browser shows its own wording — so this is only about the prompt appearing at all.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const value = useMemo<UnsavedValue>(() => ({ dirty, setDirty, leave }), [dirty, leave]);

  const discard = () => {
    const { go } = pending ?? {};
    // Cleared before leaving, so the destination doesn't inherit a flag about a draft that is
    // being abandoned on the way there.
    setDirty(false);
    setPending(null);
    go?.();
  };

  return (
    <UnsavedContext.Provider value={value}>
      {children}
      {pending ? (
        <Modal
          title="Leave without saving?"
          subtitle="Your changes on this screen haven't been saved."
          onClose={() => setPending(null)}
          footer={
            <>
              <button type="button" onClick={() => setPending(null)} className={btnGhost}>
                Keep editing
              </button>
              {/* The destructive option is the one that leaves, so it does not get the primary
                  green. Staying is the safe answer and the likelier intent. */}
              <button
                type="button"
                onClick={discard}
                className="flex-1 rounded-lg border border-chip-500/40 py-2 text-sm font-medium text-chip-500 hover:bg-chip-500/5"
              >
                Discard changes
              </button>
            </>
          }
        >
          <p className="text-sm text-ink-800/70">
            Nothing you&apos;ve changed here has been sent to the server yet. Leaving now discards
            it.
          </p>
        </Modal>
      ) : null}
    </UnsavedContext.Provider>
  );
}
